/* eslint-disable max-len */
import { readStoredExportFormat } from 'Pods/ThreeDStudio/Services/exportFormatStorage'
import { openedModelToStatePatch } from 'Pods/ThreeDStudio/Services/openedModelToStatePatch/openedModelToStatePatch'
import {
  DEFAULT_UI,
  VIEWER_SIDEBAR_TAB,
  IMAGE_ASSISTANT_TAB,
  MOBILE_PANEL,
  ACTION,
  DEFAULT_SCHEMA,
  DEFAULT_VIEWER,
  DEFAULT_REMESH_SETTINGS,
  DEFAULT_TEXTURE_SETTINGS,
  DEFAULT_MODEL_SETTINGS,
  DEFAULT_EXPORT,
  DEFAULT_GENERATION,
  USE_CASE,
  MODEL_FORMAT_EXPORT,
  STUDIO_ACCESS_TIER,
  USAGE_MODAL_REASON,
} from 'Pods/ThreeDStudio/Store/constants'

import { threeDStudioStore } from '../index'
import { EXPORT_STATUS } from '../slices/export'

export const setUiState = (patch, schemaPatch) => {
  threeDStudioStore.setState((state) => ({
    ui: { ...state.ui, ...patch },
    ...(schemaPatch && { schema: { ...state.schema, ...schemaPatch }}),
  }))
}

/**
 * Sets the active viewer sidebar tab
 * @param {string | undefined} activeViewerSidebarTab - One of: "remesh", "texture", "segment", "animate", "render", "assist" or undefined
 */
export const setActiveViewerSidebarTab = (activeViewerSidebarTab) => {
  const { ui } = threeDStudioStore.getState()
  const { isViewerSidebarOpen, activeViewerSidebarTab: currentActiveViewerSidebarTab } = ui
  const enhancedAction = {
    [VIEWER_SIDEBAR_TAB.REMESH]: ACTION.MESH_SIMPLIFICATION,
    [VIEWER_SIDEBAR_TAB.TEXTURE]: ACTION.RETEXTURE,
    [VIEWER_SIDEBAR_TAB.SEGMENT]: ACTION.MESH_SEGMENTATION,
    [VIEWER_SIDEBAR_TAB.ANIMATE]: null,
    [VIEWER_SIDEBAR_TAB.RENDER]: null,
    [VIEWER_SIDEBAR_TAB.VARIATIONS]: null,
    [VIEWER_SIDEBAR_TAB.ASSIST]: null,
  }[activeViewerSidebarTab]

  if (!isViewerSidebarOpen) {
    setUiState(
      {
        activeViewerSidebarTab,
        isViewerSidebarOpen: true,
        isModelSettingsOpen: false,
      },
      { enhancedAction },
    )

    return
  }

  if (currentActiveViewerSidebarTab === activeViewerSidebarTab) {
    setUiState(
      { isViewerSidebarOpen: false, activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab },
      { enhancedAction: DEFAULT_SCHEMA.enhancedAction },
    )

    return
  }

  setUiState({ activeViewerSidebarTab }, { enhancedAction })
}

/**
 * Sets whether the model settings sidebar (left side) is open or collapsed.
 * @param {boolean} isModelSettingsOpen
 */
export const setIsModelSettingsOpen = (isModelSettingsOpen) => {
  if (isModelSettingsOpen) {
    setUiState({
      isModelSettingsOpen,
      isViewerSidebarOpen: false,
      activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab,
    })

    return
  }

  setUiState({ isModelSettingsOpen })
}

/**
 * Sets whether the left (library) sidebar is open or collapsed.
 * @param {boolean} isSidebarOpen
 */
export const setIsSidebarOpen = (isSidebarOpen) => {
  setUiState({ isSidebarOpen })
}

/**
 * Opens the viewer sidebar on the Variations tab. Idempotent: unlike
 * setActiveViewerSidebarTab it never toggles the panel closed when Variations is
 * already active, so it is safe to call from completion handlers.
 */
export const openVariationsTab = () => {
  setUiState({
    isViewerSidebarOpen: true,
    activeViewerSidebarTab: VIEWER_SIDEBAR_TAB.VARIATIONS,
    isModelSettingsOpen: false,
  })
}

/**
 * Sets whether the viewer sidebar is open or collapsed.
 * @param {boolean} isViewerSidebarOpen
 */
export const setIsViewerSidebarOpen = (isViewerSidebarOpen) => {
  if (isViewerSidebarOpen) {
    setUiState(
      {
        activeViewerSidebarTab: VIEWER_SIDEBAR_TAB.REMESH,
        isViewerSidebarOpen: true,
        isModelSettingsOpen: false,
      },
      { enhancedAction: ACTION.MESH_SIMPLIFICATION },
    )

    return
  }

  setUiState(
    { isViewerSidebarOpen: false, activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab },
    { enhancedAction: DEFAULT_SCHEMA.enhancedAction },
  )
}

/**
 * Selects the active work panel on small screens (< md), where the three
 * desktop panels are presented as tabs. Also syncs the desktop open flags so
 * the conditionally-rendered content of each tab is actually mounted.
 * @param {string} activeMobilePanel - One of MOBILE_PANEL values
 */
export const setActiveMobilePanel = (activeMobilePanel) => {
  if (activeMobilePanel === MOBILE_PANEL.GENERATE) {
    setUiState(
      {
        activeMobilePanel,
        isModelSettingsOpen: true,
        isViewerSidebarOpen: false,
        activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab,
      },
      { enhancedAction: DEFAULT_SCHEMA.enhancedAction },
    )

    return
  }

  if (activeMobilePanel === MOBILE_PANEL.ENHANCE) {
    const { activeViewerSidebarTab } = threeDStudioStore.getState().ui
    const nextTab = activeViewerSidebarTab ?? VIEWER_SIDEBAR_TAB.REMESH

    setUiState(
      {
        activeMobilePanel,
        isViewerSidebarOpen: true,
        isModelSettingsOpen: false,
        activeViewerSidebarTab: nextTab,
      },
      nextTab === VIEWER_SIDEBAR_TAB.REMESH ? { enhancedAction: ACTION.MESH_SIMPLIFICATION } : undefined,
    )

    return
  }

  // MOBILE_PANEL.VIEWER — the viewer always renders, so no flags to force.
  setUiState({ activeMobilePanel })
}

/**
 * Sets the active texture tab inside Texture viewer sidebar.
 * @param {string} activeTextureTab - One of: "generate", "resolution", "edit"
 */
export const setActiveTextureTab = (activeTextureTab) => {
  setUiState({ activeTextureTab })
}

/**
 * Sets the active action tab inside Texture generate panel.
 * @param {string} activeTextureActionTab - One of: "image", "text"
 */
export const setActiveTextureActionTab = (activeTextureActionTab) => {
  setUiState({ activeTextureActionTab })
}

/**
 * Extracts the generation panel patch from an enhancement run's source run.
 * Enhancement runs carry no generation settings of their own, so the source
 * run that produced the mesh seeds the Generate 3D model panel instead.
 * Undefined values are dropped so fields the source run serializer omits
 * (e.g. private) keep their defaults.
 * @param {Object} sourceRun - Serialized source run from the run payload
 * @returns {Object|null} - { modelSettings, schema } or null when unparseable
 */
const sourceRunToGenerationPatch = (sourceRun) => {
  const sourcePatch = openedModelToStatePatch(sourceRun)
  if (!sourcePatch.modelSettings) {
    return null
  }

  const modelSettings = Object.fromEntries(
    Object.entries(sourcePatch.modelSettings).filter(([, value]) => value !== undefined)
  )
  const { generationAction, generationProvider } = sourcePatch.schema || {}

  return {
    modelSettings,
    schema: {
      ...(generationAction && { generationAction }),
      ...(generationProvider && { generationProvider }),
    },
  }
}

/**
 * Sets the opened model (e.g. selected library item) and syncs modelSettings
 * and optionSettings from the run payload so the form reflects the opened run.
 * @param {Object} openedModel - Model object; use {} to clear
 * @param {Object} options
 * @param {boolean} options.keepVariationsTab - Keep the variations tab open
 * (set when the open originates from a variation card click)
 */
export const setOpenedModel = (openedModel, { keepVariationsTab = false } = {}) => {
  const patch = openedModelToStatePatch(openedModel)
  const sourcePatch = patch.schema?.enhancedAction && openedModel?.sourceRun
    ? sourceRunToGenerationPatch(openedModel.sourceRun)
    : null
  // On small screens, surfacing a model means the viewer tab should take over.
  const hasModel = !!openedModel && Object.keys(openedModel).length > 0
  threeDStudioStore.setState((state) => {
    // Switching variations from the variations tab keeps it open instead of
    // letting the per-model parsers close the sidebar or steal the tab.
    const preserveVariationsTab = keepVariationsTab && hasModel &&
      state.ui?.isViewerSidebarOpen &&
      state.ui?.activeViewerSidebarTab === VIEWER_SIDEBAR_TAB.VARIATIONS
    const exportState = state.export.status === EXPORT_STATUS.ERROR
      ? { ...DEFAULT_EXPORT, selectedFormat: state.export.selectedFormat }
      : state.export
    // Opening a 3D print model defaults the download to STL, unless the user has
    // a remembered format (which always wins). This default is not persisted.
    const isThreeDPrint = patch.modelSettings?.selectedUseCase === USE_CASE.THREE_D_PRINT
    const threeDPrintExport = isThreeDPrint && readStoredExportFormat() == null
      ? { selectedFormat: MODEL_FORMAT_EXPORT.STL }
      : null
    // Surfacing a different model replaces what the assistant was working against,
    // so the panel is dismissed rather than left floating over the new model. Gated
    // on the id changing so re-hydrating the same run (URL sync, run completion)
    // does not close the panel out from under the user.
    const isAnotherModel = hasModel
      && String(patch.openedModel?.id) !== String(state.ui?.openedModel?.id)
    return {
      ...state,
      ui: {
        ...state.ui,
        ...patch.ui,
        openedModel: patch.openedModel,
        // Kept as its own key (not read from openedModel) so consumers do not
        // depend on the raw run payload; resets when the model is cleared.
        variationsCount: patch.openedModel?.variationsCount,
        ...(hasModel && { activeMobilePanel: MOBILE_PANEL.VIEWER }),
        ...(isAnotherModel && {
          isImageAssistantOpen: false,
          imageAssistantTab: IMAGE_ASSISTANT_TAB.SEARCH,
          editImage: null,
          // The new model brings its own session, so the previous conversation must
          // not be restored over it.
          imageAssistantSessionId: undefined,
        }),
        ...(preserveVariationsTab && {
          isViewerSidebarOpen: true,
          activeViewerSidebarTab: VIEWER_SIDEBAR_TAB.VARIATIONS,
          isModelSettingsOpen: false,
        }),
      },
      viewer: { ...DEFAULT_VIEWER, ...(patch.viewer || {}) },
      schema: { ...state.schema, ...(sourcePatch?.schema || {}), ...patch.schema },
      modelSettings: {
        ...DEFAULT_MODEL_SETTINGS,
        ...(patch.modelSettings || sourcePatch?.modelSettings || {}),
      },
      remeshSettings: { ...DEFAULT_REMESH_SETTINGS, ...(patch.remeshSettings || {}) },
      textureSettings: { ...DEFAULT_TEXTURE_SETTINGS, ...(patch.textureSettings || {}) },
      // Pending exports survive model switches so the completion subscription
      // stays alive; stale export errors reset like the rest of the model UI.
      export: { ...exportState, ...threeDPrintExport, ...(patch.export || {}) },
      generation: {
        ...state.generation,
        ...patch.generation,
        hasFailed: false,
        errorDetails: {},
        errorId: '',
      },
    }
  })
}

/**
 * Updates opened model title (prompt) in the store.
 * @param {string} prompt
 */
export const setOpenedModelTitle = (title) => {
  threeDStudioStore.setState(state => ({
    ui: {
      ...state.ui,
      openedModel: {
        ...(state.ui?.openedModel || {}),
        title,
      },
    },
  }))
}

/**
 * Sets the opened model's comment count so the footer badge stays in sync
 * without a page refresh. The comments API returns meta.commentsCount (all
 * comments including replies) on create and destroy; no-ops for a non-number
 * so a partial payload during the BE rollout leaves the badge untouched.
 * @param {number} commentsCount
 */
export const setOpenedModelCommentsCount = (commentsCount) => {
  if (typeof commentsCount !== 'number') {
    return
  }

  threeDStudioStore.setState(state => ({
    ui: {
      ...state.ui,
      openedModel: {
        ...(state.ui?.openedModel || {}),
        commentsCount,
      },
    },
  }))
}

/**
 * Decrements the opened model's variations count after a variation delete so
 * the sidebar badge stays in sync without a refetch. Clamped at zero; no-ops
 * when no count is known.
 */
export const decrementVariationsCount = () => {
  const { variationsCount } = threeDStudioStore.getState().ui
  if (typeof variationsCount !== 'number') {
    return
  }

  setUiState({ variationsCount: Math.max(0, variationsCount - 1) })
}

/**
 * Shallow-merges a partial settings patch into openedModel.settings.
 * Used after a successful PATCH to /sessions/{id}/settings so the zustand
 * snapshot mirrors the server without a refetch. Keys are camelCase to match
 * the serializer response shape.
 * @param {Object} settingsPatch - Partial settings (e.g. { showGrid: true })
 */
export const setOpenedModelSettingsPatch = (settingsPatch) => {
  if (!settingsPatch || typeof settingsPatch !== 'object' || Array.isArray(settingsPatch)) {
    return
  }

  threeDStudioStore.setState(state => ({
    ui: {
      ...state.ui,
      openedModel: {
        ...(state.ui?.openedModel || {}),
        settings: {
          ...(state.ui?.openedModel?.settings || {}),
          ...settingsPatch,
        },
      },
    },
  }))
}

/**
 * Sets whether a file is being uploaded
 * @param {boolean} isModelUploading
 */
export const setIsModelUploading = (isModelUploading) => {
  setUiState({ isModelUploading })
}

/**
 * Sets whether an image is being uploaded
 * @param {boolean} isImageUploading
 */
export const setIsImageUploading = (isImageUploading) => {
  setUiState({ isImageUploading })
}

/**
 * Opens the AI Assistant image panel on the given tab.
 * @param {string} tab - One of IMAGE_ASSISTANT_TAB values (defaults to Search)
 */
export const openImageAssistant = (tab = IMAGE_ASSISTANT_TAB.SEARCH) => {
  setUiState({ isImageAssistantOpen: true, imageAssistantTab: tab })
}

export const closeImageAssistant = () => {
  // Dismissed AI Assistant state. The panel reopens on Search rather than the last
  // tab, and the image handed to the Edit tab is dropped so it reopens empty. Shared
  // by closeImageAssistant and setOpenedModel so the two resets cannot drift.
  setUiState({
    isImageAssistantOpen: false,
    imageAssistantTab: IMAGE_ASSISTANT_TAB.SEARCH,
    editImage: null,
    editReturnTab: null,
  })
}

/**
 * Switches the active tab inside the AI Assistant image panel.
 * @param {string} tab - One of IMAGE_ASSISTANT_TAB values
 */
export const setImageAssistantTab = (tab) => {
  setUiState({ imageAssistantTab: tab })
}

/**
 * Records the generator session the AI Assistant's image runs belong to, so closing
 * and reopening the panel can restore the conversation from that session. Deliberately
 * separate from generation.sessionId, which the 3D run lifecycle resets.
 * @param {number|string|undefined} imageAssistantSessionId
 */
export const setImageAssistantSessionId = (imageAssistantSessionId) => {
  setUiState({ imageAssistantSessionId })
}

/**
 * Stores the image handed off to the Edit tab (from a Create result or a Search
 * selection) so the Edit tab can load it onto the mask-painting canvas.
 * @param {{ id: number, url: string, alt?: string }} editImage
 */
export const setEditImage = (editImage) => {
  setUiState({ editImage })
}

/**
 * Hands an image to the Edit screen and switches the open panel to it, recording where
 * "Exit Edit mode" should go back to.
 * @param {{ id: number, url: string, alt?: string }} editImage
 * @param {string|null} editReturnTab - IMAGE_ASSISTANT_TAB to return to, or null to
 *   close the panel on exit (the ideation entry, which opened the panel straight into Edit)
 */
export const openImageEditor = (editImage, editReturnTab = null) => {
  setUiState({
    editImage,
    editReturnTab,
    imageAssistantTab: IMAGE_ASSISTANT_TAB.EDIT,
    isImageAssistantOpen: true,
  })
}

/**
 * Leaves the Edit screen the way it was entered: back to the recorded tab, or by
 * closing the panel when Edit was opened directly from the ideation flow.
 */
export const exitImageEditor = () => {
  const { ui } = threeDStudioStore.getState()

  if (ui.editReturnTab) {
    setImageAssistantTab(ui.editReturnTab)
  } else {
    closeImageAssistant()
  }
}

/**
 * Loads a freshly uploaded input image onto the Edit tab's canvas, but only while that tab
 * is the one on screen. Its empty state asks the user to upload an image, so the upload is
 * the hand-off itself and clicking "Edit image" afterwards would be a redundant step.
 * @param {{ id: number, url: string }} image
 */
export const openUploadedImageInEditTab = (image) => {
  const { ui } = threeDStudioStore.getState()
  if (!ui.isImageAssistantOpen || ui.imageAssistantTab !== IMAGE_ASSISTANT_TAB.EDIT) {
    return
  }

  setEditImage(image)
}

/**
 * Opens the usage modal.
 * @param {string | null} pendingGenerationTrigger - Trigger key of the generate
 * button whose click opened the modal, when the generation should auto-start
 * once in-modal enrollment (e.g. the free trial) unlocks it. Callers that open
 * the modal without generation intent (sidebar usage button) leave it null,
 * which also clears any stale pending trigger.
 */
export const openUsageModal = (pendingGenerationTrigger = null) => {
  setUiState({ isUsageModalOpen: true, usageModalReason: null, pendingGenerationTrigger })
}

/**
 * Opens the usage modal in "paid feature" mode: a locked control (Private mode,
 * DCC Bridge, Export) was clicked, so the modal shows the upgrade body with the
 * advanced-features list regardless of the user's tier. There is no pending
 * generation to resume, so the trigger is cleared.
 */
export const openPaidFeatureModal = () => {
  setUiState({
    isUsageModalOpen: true,
    usageModalReason: USAGE_MODAL_REASON.PAID_FEATURE,
    pendingGenerationTrigger: null,
  })
}

export const closeUsageModal = () => {
  // A close while the user is still eligible means they dismissed the modal
  // without enrolling, so the pending auto-generation is abandoned. After a
  // successful enrollment the tier has already flipped, so the trigger
  // survives the close and the generate button consumes it.
  const { studioAccessTier } = threeDStudioStore.getState().subscription
  setUiState({
    isUsageModalOpen: false,
    usageModalReason: null,
    ...(studioAccessTier === STUDIO_ACCESS_TIER.ELIGIBLE_FOR_TRIAL && { pendingGenerationTrigger: null }),
  })
}

export const clearPendingGenerationTrigger = () => {
  setUiState({ pendingGenerationTrigger: null })
}

/**
 * Arms the post-account-creation generation replay. Set by the resume hook once a
 * pending generation saved before a logged-out generate click is restored from
 * localStorage: the matching GenerateButton consumes the trigger and replays its
 * click so the generation (or enhancement) continues where the user left off.
 * @param {string | null} pendingPostAuthGenerationTrigger - Trigger key of the
 * button to replay (GENERATION_TRIGGER_KEY or an enhancement tab key)
 */
export const setPendingPostAuthGenerationTrigger = (pendingPostAuthGenerationTrigger) => {
  setUiState({ pendingPostAuthGenerationTrigger })
}

export const clearPendingPostAuthGenerationTrigger = () => {
  setUiState({ pendingPostAuthGenerationTrigger: null })
}

/**
 * Opens the /ai sign in / sign up modal.
 * @param {'signup' | 'login'} mode - Which screen to open on (default 'signup')
 * @param {'auth' | 'code' | 'success'} step - Which step to open on (default 'auth')
 */
export const openSignInModal = (mode = 'signup', step = 'auth') => {
  setUiState({ isSignInModalOpen: true, signInModalMode: mode, signInModalStep: step })
}

export const closeSignInModal = () => {
  setUiState({ isSignInModalOpen: false })
}

/**
 * Opens the comments modal for the currently opened model. The modal reads the
 * run id and title straight from state.ui.openedModel, so no argument is needed.
 */
export const openCommentsModal = () => {
  setUiState({ isCommentsModalOpen: true })
}

export const closeCommentsModal = () => {
  setUiState({ isCommentsModalOpen: false })
}

/**
 * Surgically clears the opened model without touching form state.
 *
 * Used by the URL-sync hook when the URL leaves a model detail page
 * (e.g. /ai or /ai-3d-model-generator/3d-models). Distinct from clearModelSettings, which
 * also resets the user's form draft and is reserved for explicit
 * "Generate New 3D Model" / "+" actions.
 */
export const clearOpenedModel = () => {
  setUiState({
    openedModel: {},
    variationsCount: undefined,
    // The assistant was working against the model being left behind, so it goes with it
    // rather than staying open over the fresh composition - same reasoning as the
    // model-switch reset in setOpenedModel, including dropping the previous session so
    // its conversation is not restored into the new model's panel.
    isImageAssistantOpen: false,
    imageAssistantTab: IMAGE_ASSISTANT_TAB.SEARCH,
    editImage: null,
    imageAssistantSessionId: undefined,
  })
}

/**
 * Full reset for when the currently opened model is deleted. Restores every slice
 * setOpenedModel populates (viewer, schema, form drafts, enhancement settings,
 * export, generation) to its default so no data from the removed model lingers.
 * The staticViewer instance is intentionally preserved so the live viewer is reused.
 *
 * The generation slice is preserved while a run is in flight: the deleted model is
 * always a different, completed one (the in-flight card has no delete button), and
 * its runId keys the live GeneratorRunsChannel subscription. Clearing it would tear
 * the subscription down and orphan the running generation.
 *
 * availableCredits is always preserved: it is the user's live wallet balance (global
 * state seeded on load and updated by run completions), not data setOpenedModel
 * populates. Resetting it to null let the GenerateButton credits gate read 0 and open
 * the upgrade modal after deleting a just-generated model, even with credits left.
 */
export const resetOpenedModelState = () => {
  threeDStudioStore.setState((state) => {
    const hasRunInFlight = state.generation.isGenerating || state.generation.isEnhancing
    return {
      ui: {
        ...state.ui,
        openedModel: {},
        variationsCount: undefined,
        isModelSettingsOpen: true,
        isViewerSidebarOpen: false,
        activeViewerSidebarTab: DEFAULT_UI.activeViewerSidebarTab,
      },
      viewer: { ...DEFAULT_VIEWER },
      schema: { ...DEFAULT_SCHEMA },
      modelSettings: { ...DEFAULT_MODEL_SETTINGS },
      remeshSettings: { ...DEFAULT_REMESH_SETTINGS },
      textureSettings: { ...DEFAULT_TEXTURE_SETTINGS },
      export: { ...DEFAULT_EXPORT },
      generation: hasRunInFlight
        ? state.generation
        : { ...DEFAULT_GENERATION, availableCredits: state.generation.availableCredits },
    }
  })
}

/**
 * Toggles whether the Advanced settings group is expanded.
 */
export const toggleAdvancedSettingsExpanded = () => {
  const { isAdvancedSettingsExpanded } = threeDStudioStore.getState().ui
  setUiState({ isAdvancedSettingsExpanded: !isAdvancedSettingsExpanded })
}
